import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { ChevronRight, ChevronLeft, X, Sparkles } from "lucide-react";

const TOUR_KEY = "callintell_tour_completed";

const STEPS = [
  {
    target: '[data-testid="sidebar"]',
    title: "Navigation Sidebar",
    content: "Welcome to Callintell! This sidebar gives you quick access to every section. Let's walk through each one.",
    position: "right",
  },
  {
    target: '[data-testid="nav-dashboard"]',
    title: "Dashboard",
    content: "Your Dashboard shows a real-time overview — total calls, valid vs fake breakdown, and publisher risk charts.",
    position: "right",
  },
  {
    target: '[data-testid="nav-analytics"]',
    title: "Analytics",
    content: "Track trends for call volume, fake call percentages, and quality scores over custom date ranges.",
    position: "right",
  },
  {
    target: '[data-testid="nav-publisher-report"]',
    title: "Publisher Report",
    content: "See each publisher's performance — valid rate, fake rate, total calls, and risk classification at a glance.",
    position: "right",
  },
  {
    target: '[data-testid="nav-publisher-ranking"]',
    title: "Publisher Ranking",
    content: "Publishers are ranked with a weighted score based on valid rate, conversions, duration, and fraud indicators.",
    position: "right",
  },
  {
    target: '[data-testid="nav-call-analysis"]',
    title: "Call Analysis",
    content: "This is where your AI-analyzed calls live. Filter by category (Valid, Fake, Adult, Non-US), search by number, and view detailed reports.",
    position: "right",
  },
  {
    target: '[data-testid="nav-profile"]',
    title: "Profile",
    content: "View your account info, minutes balance, and Ringba API settings. Change your password here too.",
    position: "right",
  },
  {
    target: '[data-testid="nav-buy-credits"]',
    title: "Buy Minutes",
    content: "Purchase analysis minutes via USDT (TRC20) to power AI call analysis. Choose from Starter, Growth, Pro, or Enterprise packages.",
    position: "right",
  },
  {
    target: '[data-testid="notification-bell"]',
    title: "Notifications",
    content: "Important announcements appear here. A red badge shows your unread count.",
    position: "bottom",
  },
  {
    target: '[data-testid="header-support-btn"]',
    title: "Contact Support",
    content: "Need help? Click here to reach our support team directly via Microsoft Teams. We're here for you!",
    position: "bottom",
  },
];

function getTooltipPosition(targetEl, position) {
  if (!targetEl) return { top: 0, left: 0 };
  const rect = targetEl.getBoundingClientRect();
  const gap = 12;
  const tooltipW = 360;
  const tooltipH = 180;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const pad = 16;

  let pos;
  switch (position) {
    case "right":
      pos = { top: rect.top + rect.height / 2, left: rect.right + gap, transform: "translateY(-50%)" };
      break;
    case "left":
      pos = { top: rect.top + rect.height / 2, left: rect.left - gap, transform: "translate(-100%, -50%)" };
      break;
    case "bottom":
      pos = { top: rect.bottom + gap, left: rect.left + rect.width / 2, transform: "translateX(-50%)" };
      break;
    case "top":
      pos = { top: rect.top - gap, left: rect.left + rect.width / 2, transform: "translate(-50%, -100%)" };
      break;
    default:
      pos = { top: rect.bottom + gap, left: rect.left };
  }

  // Clamp to viewport
  if (position === "right" && pos.left + tooltipW > vw - pad) {
    pos = { top: rect.bottom + gap, left: Math.min(rect.left, vw - tooltipW - pad), transform: "none" };
  }
  if (position === "left" && pos.left - tooltipW < pad) {
    pos = { top: rect.bottom + gap, left: Math.max(pad, rect.left), transform: "none" };
  }
  if (position === "bottom" || position === "top") {
    const estLeft = pos.left - tooltipW / 2;
    if (estLeft < pad) pos.left = pad + tooltipW / 2;
    if (estLeft + tooltipW > vw - pad) pos.left = vw - pad - tooltipW / 2;
  }
  // Vertical clamp
  const estTop = position === "bottom" ? pos.top : (position === "top" ? pos.top - tooltipH : pos.top - tooltipH / 2);
  if (estTop < pad) pos.top = pad + (position === "right" || position === "left" ? tooltipH / 2 : 0);
  if (pos.top + tooltipH > vh - pad && position === "bottom") {
    pos = { top: rect.top - gap - tooltipH, left: pos.left, transform: pos.transform };
  }

  return pos;
}

function getArrowStyle(position) {
  const base = { position: "absolute", width: 0, height: 0 };
  const color = "#1a1f2e";
  switch (position) {
    case "right":
      return { ...base, left: -8, top: "50%", transform: "translateY(-50%)", borderTop: "8px solid transparent", borderBottom: "8px solid transparent", borderRight: `8px solid ${color}` };
    case "left":
      return { ...base, right: -8, top: "50%", transform: "translateY(-50%)", borderTop: "8px solid transparent", borderBottom: "8px solid transparent", borderLeft: `8px solid ${color}` };
    case "bottom":
      return { ...base, top: -8, left: "50%", transform: "translateX(-50%)", borderLeft: "8px solid transparent", borderRight: "8px solid transparent", borderBottom: `8px solid ${color}` };
    case "top":
      return { ...base, bottom: -8, left: "50%", transform: "translateX(-50%)", borderLeft: "8px solid transparent", borderRight: "8px solid transparent", borderTop: `8px solid ${color}` };
    default:
      return base;
  }
}

export default function OnboardingTour({ forceStart = false, onComplete }) {
  const { user } = useAuth();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);

  const endTour = useCallback(() => {
    setActive(false);
    setStep(0);
    if (user) localStorage.setItem(`${TOUR_KEY}_${user.id}`, "true");
    // Remove any spotlight
    document.querySelectorAll("[data-tour-spotlight]").forEach((el) => {
      el.style.removeProperty("position");
      el.style.removeProperty("z-index");
      el.style.removeProperty("box-shadow");
    });
    if (onComplete) onComplete();
  }, [user, onComplete]);

  useEffect(() => {
    if (forceStart) {
      setStep(0);
      setActive(true);
      return;
    }
    if (user && user.role !== "admin") {
      const completed = localStorage.getItem(`${TOUR_KEY}_${user.id}`);
      if (!completed) {
        const timer = setTimeout(() => setActive(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [user, forceStart]);

  // Listen for restart-tour event from Profile page
  useEffect(() => {
    const handleRestartTour = () => {
      setStep(0);
      setActive(true);
    };
    window.addEventListener("callintell-restart-tour", handleRestartTour);
    return () => window.removeEventListener("callintell-restart-tour", handleRestartTour);
  }, []);

  useEffect(() => {
    if (!active) return;
    const currentStep = STEPS[step];
    if (!currentStep) return;

    const el = document.querySelector(currentStep.target);
    if (!el) return;

    // Spotlight the target
    document.querySelectorAll("[data-tour-spotlight]").forEach((prev) => {
      prev.removeAttribute("data-tour-spotlight");
      prev.style.removeProperty("position");
      prev.style.removeProperty("z-index");
      prev.style.removeProperty("box-shadow");
    });

    el.setAttribute("data-tour-spotlight", "true");
    const origPos = window.getComputedStyle(el).position;
    if (origPos === "static") el.style.position = "relative";
    el.style.zIndex = "10001";
    el.style.boxShadow = "0 0 0 4px rgba(59, 130, 246, 0.3), 0 0 20px rgba(59, 130, 246, 0.1)";
    el.style.borderRadius = "8px";

    // Scroll element into view
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });

    // Position the tooltip
    setTimeout(() => {
      const pos = getTooltipPosition(el, currentStep.position);
      setTooltipPos(pos);
    }, 100);
  }, [active, step]);

  // Escape key to close
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape" && active) endTour(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [active, endTour]);

  if (!active || !user || user.role === "admin") return null;

  const currentStep = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  return (
    <>
      {/* Overlay */}
      <div
        data-testid="tour-overlay"
        className="fixed inset-0 z-[10000] bg-black/60 transition-opacity duration-300"
        onClick={endTour}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        data-testid="tour-tooltip"
        className="fixed z-[10002] animate-in fade-in slide-in-from-bottom-2 duration-300"
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          transform: tooltipPos.transform,
          maxWidth: "min(360px, calc(100vw - 2rem))",
        }}
      >
        {/* Arrow */}
        <div style={getArrowStyle(currentStep.position)} />

        {/* Card */}
        <div className="bg-[#1a1f2e] border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-1">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-blue-400" />
              <span className="text-[13px] font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                {currentStep.title}
              </span>
            </div>
            <button
              onClick={endTour}
              data-testid="tour-close"
              className="h-6 w-6 flex items-center justify-center rounded-md text-slate-500 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-5 py-3">
            <p className="text-[13px] leading-relaxed text-slate-400">{currentStep.content}</p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-white/5 bg-white/[0.02]">
            {/* Progress */}
            <div className="flex items-center gap-1">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === step ? "w-4 bg-blue-500" : i < step ? "w-1.5 bg-blue-500/40" : "w-1.5 bg-slate-600"
                  }`}
                />
              ))}
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2">
              {isFirst ? (
                <button
                  onClick={endTour}
                  className="text-[12px] text-slate-500 hover:text-slate-300 transition-colors px-2 py-1"
                  data-testid="tour-skip"
                >
                  Skip tour
                </button>
              ) : (
                <button
                  onClick={() => setStep(step - 1)}
                  className="flex items-center gap-1 text-[12px] text-slate-400 hover:text-white transition-colors px-2 py-1"
                  data-testid="tour-back"
                >
                  <ChevronLeft className="h-3 w-3" />
                  Back
                </button>
              )}
              <button
                onClick={() => (isLast ? endTour() : setStep(step + 1))}
                className="flex items-center gap-1 text-[12px] font-medium text-white bg-blue-600 hover:bg-blue-500 px-3.5 py-1.5 rounded-lg transition-colors"
                data-testid="tour-next"
              >
                {isLast ? "Got it!" : "Next"}
                {!isLast && <ChevronRight className="h-3 w-3" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export { TOUR_KEY };
